import React, { useState, useEffect } from 'react';

// ============================================
// TYPE DEFINITIONS
// ============================================
interface Store {
  storeId: string;
  storeName: string;
  businessId: string;
  manager: string;
  email: string;
  latitude: number;
  longitude: number;
  isActive: boolean;
}

interface GPSLocation {
  latitude: number;
  longitude: number;
}

interface TagInfo {
  tagId: string;
  productId: string;
  batchId: string;
  productName: string;
  status: string;
  isActivated: boolean;
  warrantyActive: boolean;
  lotteryEnrolled: boolean;
}

interface ActivationResult {
  success: boolean;
  message: string;
  type: 'warranty' | 'lottery';
  lotteryNumber?: string;
  lotteryName?: string;
  warrantyExpiry?: string;
}

// ============================================
// STORE TAG ACTIVATION — WARRANTY + LOTTERY
// ============================================
const StoreActivateTag: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const [store, setStore] = useState<Store | null>(null);
  const [tagId, setTagId] = useState('');
  const [tagInfo, setTagInfo] = useState<TagInfo | null>(null);
  const [tagLoading, setTagLoading] = useState(false);
  const [gpsLocation, setGpsLocation] = useState<GPSLocation | null>(null);
  const [gpsChecking, setGpsChecking] = useState(false);
  const [warrantyLoading, setWarrantyLoading] = useState(false);
  const [lotteryLoading, setLotteryLoading] = useState(false);
  const [results, setResults] = useState<ActivationResult[]>([]);
  
  // Customer info for warranty
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  // ── Load store from localStorage ──
  useEffect(() => {
    const storeData = localStorage.getItem('storeData');
    if (!storeData) {
      if (onNavigate) onNavigate('login');
      return;
    }
    setStore(JSON.parse(storeData) as Store);
    getGPSLocation();
  }, [onNavigate]);

  // ── GPS ──
  const getGPSLocation = () => {
    setGpsChecking(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setGpsChecking(false);
        },
        () => {
          setGpsChecking(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGpsChecking(false);
    }
  };

  // ── Look up tag info when tagId changes ──
  useEffect(() => {
    if (!tagId || tagId.length < 3) {
      setTagInfo(null);
      return;
    }

    const debounce = setTimeout(async () => {
      setTagLoading(true);
      setResults([]);
      try {
        const token = localStorage.getItem('storeToken');
        // Try store-lottery tag lookup first, fallback to tags endpoint
        let res = await fetch(`/api/store-lottery/tag-lookup/${tagId.toUpperCase()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        // Fallback to tags endpoint
        if (!res.ok) {
          res = await fetch(`/api/tags/${tagId.toUpperCase()}/lookup`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        }
        
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            setTagInfo({
              tagId: data.data.tagId || tagId.toUpperCase(),
              productId: data.data.productId || '',
              batchId: data.data.batchId || '',
              productName: data.data.productName || data.data.product?.name || 'Unknown Product',
              status: data.data.status || 'unknown',
              isActivated: data.data.status === 'activated' || data.data.status === 'sold',
              warrantyActive: !!data.data.warranty?.active || !!data.data.sale?.soldAt,
              lotteryEnrolled: !!data.data.lottery?.enrolled,
            });
          } else {
            // Tag found but minimal info — still allow activation
            setTagInfo({
              tagId: tagId.toUpperCase(),
              productId: '',
              batchId: '',
              productName: 'Tag Found',
              status: 'imported',
              isActivated: false,
              warrantyActive: false,
              lotteryEnrolled: false,
            });
          }
        } else if (res.status === 404) {
          setTagInfo(null);
          setResults([{ success: false, message: 'Tag not found in system', type: 'warranty' }]);
        } else {
          // Fallback: allow manual entry even if lookup fails
          setTagInfo({
            tagId: tagId.toUpperCase(),
            productId: '',
            batchId: '',
            productName: 'Manual Entry',
            status: 'unknown',
            isActivated: false,
            warrantyActive: false,
            lotteryEnrolled: false,
          });
        }
      } catch {
        // Network error — still allow activation attempt
        setTagInfo({
          tagId: tagId.toUpperCase(),
          productId: '',
          batchId: '',
          productName: 'Manual Entry',
          status: 'unknown',
          isActivated: false,
          warrantyActive: false,
          lotteryEnrolled: false,
        });
      }
      setTagLoading(false);
    }, 500);

    return () => clearTimeout(debounce);
  }, [tagId]);

  // ── Activate Warranty ──
  const handleWarrantyActivate = async () => {
    if (!tagId.trim() || !gpsLocation) return;

    setWarrantyLoading(true);
    try {
      const token = localStorage.getItem('storeToken');
      const response = await fetch('/api/store-lottery/activate-warranty', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tagId: tagId.toUpperCase().trim(),
          batchId: tagInfo?.batchId || '',
          productId: tagInfo?.productId || '',
          userLatitude: gpsLocation.latitude,
          userLongitude: gpsLocation.longitude,
          customer: {
            name: customerName.trim() || undefined,
            phone: customerPhone.trim() || undefined,
            email: customerEmail.trim() || undefined,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        setResults(prev => [...prev, {
          success: true,
          message: data.message || '✅ Warranty activated successfully!',
          type: 'warranty',
          warrantyExpiry: data.data?.warrantyExpiry,
        }]);
        if (tagInfo) {
          setTagInfo({ ...tagInfo, warrantyActive: true, isActivated: true });
        }
      } else {
        setResults(prev => [...prev, {
          success: false,
          message: data.message || 'Warranty activation failed',
          type: 'warranty',
        }]);
      }
    } catch (err: any) {
      setResults(prev => [...prev, {
        success: false,
        message: err.response?.data?.message || 'Network error activating warranty',
        type: 'warranty',
      }]);
    }
    setWarrantyLoading(false);
  };

  // ── Enter Lottery ──
  const handleLotteryActivate = async () => {
    if (!tagId.trim() || !gpsLocation) return;

    setLotteryLoading(true);
    try {
      const token = localStorage.getItem('storeToken');
      const response = await fetch('/api/store-lottery/activate-tag', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tagId: tagId.toUpperCase().trim(),
          batchId: tagInfo?.batchId || '',
          productId: tagInfo?.productId || '',
          userLatitude: gpsLocation.latitude,
          userLongitude: gpsLocation.longitude,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setResults(prev => [...prev, {
          success: true,
          message: data.message || '✅ Entered lottery successfully!',
          type: 'lottery',
          lotteryNumber: data.data?.lotteryNumber,
          lotteryName: data.data?.lotteryName,
        }]);
        if (tagInfo) {
          setTagInfo({ ...tagInfo, lotteryEnrolled: true });
        }
      } else {
        setResults(prev => [...prev, {
          success: false,
          message: data.message || 'Lottery entry failed',
          type: 'lottery',
        }]);
      }
    } catch (err: any) {
      setResults(prev => [...prev, {
        success: false,
        message: err.response?.data?.message || 'Network error entering lottery',
        type: 'lottery',
      }]);
    }
    setLotteryLoading(false);
  };

  // ── Reset form ──
  const handleReset = () => {
    setTagId('');
    setTagInfo(null);
    setResults([]);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
  };

  const canActivate = tagId.trim().length > 0 && gpsLocation !== null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onNavigate ? onNavigate('dashboard') : window.location.href = '/store/dashboard'}
                className="text-gray-500 hover:text-gray-800 transition-colors text-sm"
              >
                ← Back
              </button>
              <h1 className="text-xl font-bold text-gray-900">Tag Activation</h1>
            </div>
            <div className="text-sm text-gray-500 font-medium">{store?.storeName}</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">

        {/* GPS Status */}
        <div className={`p-4 rounded-xl border ${
          gpsLocation 
            ? 'bg-emerald-50 border-emerald-200' 
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">{gpsChecking ? '🔄' : gpsLocation ? '✅' : '⚠️'}</span>
              <div>
                <p className={`font-semibold text-sm ${gpsLocation ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {gpsChecking ? 'Getting GPS...' : gpsLocation ? 'GPS Ready' : 'GPS Required'}
                </p>
                {gpsLocation && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
                  </p>
                )}
              </div>
            </div>
            {!gpsLocation && !gpsChecking && (
              <button
                onClick={getGPSLocation}
                className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-medium"
              >
                Enable GPS
              </button>
            )}
          </div>
        </div>

        {/* Tag Input */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            🏷️ Scan or Enter Tag
          </h2>
          <div className="relative">
            <input
              type="text"
              value={tagId}
              onChange={(e) => setTagId(e.target.value.trim().toUpperCase())}
              placeholder="Enter tag ID (e.g., VINGUYEN9)"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-mono"
              disabled={warrantyLoading || lotteryLoading}
            />
            {tagLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Tag Info Card */}
          {tagInfo && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-gray-900">{tagInfo.tagId}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  tagInfo.status === 'activated' || tagInfo.status === 'sold'
                    ? 'bg-green-100 text-green-700'
                    : tagInfo.status === 'imported'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tagInfo.status.toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-gray-600">{tagInfo.productName}</p>
              {tagInfo.productId && (
                <p className="text-xs text-gray-400 mt-1 font-mono">Product: {tagInfo.productId}</p>
              )}
              {tagInfo.batchId && (
                <p className="text-xs text-gray-400 font-mono">Batch: {tagInfo.batchId}</p>
              )}
              <div className="flex gap-3 mt-3">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  tagInfo.warrantyActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {tagInfo.warrantyActive ? '✅ Warranty Active' : '⬜ No Warranty'}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  tagInfo.lotteryEnrolled ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {tagInfo.lotteryEnrolled ? '🎰 In Lottery' : '⬜ No Lottery'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Customer Info (for warranty) */}
        {tagInfo && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              👤 Customer Info <span className="text-xs font-normal text-gray-400">(optional)</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Customer name"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="Phone number"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="Email address"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        )}

        {/* Two Action Buttons */}
        {tagInfo && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Warranty Button */}
            <button
              onClick={handleWarrantyActivate}
              disabled={!canActivate || warrantyLoading || tagInfo.warrantyActive}
              className={`relative overflow-hidden p-6 rounded-xl text-left transition-all ${
                tagInfo.warrantyActive
                  ? 'bg-green-50 border-2 border-green-300 cursor-default'
                  : !canActivate || warrantyLoading
                  ? 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed'
                  : 'bg-white border-2 border-blue-200 hover:border-blue-500 hover:shadow-lg cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                  tagInfo.warrantyActive ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {tagInfo.warrantyActive ? '✅' : '🛡️'}
                </div>
                <div className="flex-1">
                  <h3 className={`font-bold text-lg ${
                    tagInfo.warrantyActive ? 'text-green-800' : 'text-gray-900'
                  }`}>
                    {tagInfo.warrantyActive ? 'Warranty Active' : 'Activate Warranty'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {tagInfo.warrantyActive
                      ? 'Product warranty is already registered'
                      : 'Register product warranty for customer. Links tag to store, date, and customer.'}
                  </p>
                </div>
              </div>
              {warrantyLoading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>

            {/* Lottery Button */}
            <button
              onClick={handleLotteryActivate}
              disabled={!canActivate || lotteryLoading || tagInfo.lotteryEnrolled}
              className={`relative overflow-hidden p-6 rounded-xl text-left transition-all ${
                tagInfo.lotteryEnrolled
                  ? 'bg-purple-50 border-2 border-purple-300 cursor-default'
                  : !canActivate || lotteryLoading
                  ? 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed'
                  : 'bg-white border-2 border-purple-200 hover:border-purple-500 hover:shadow-lg cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                  tagInfo.lotteryEnrolled ? 'bg-purple-100' : 'bg-orange-100'
                }`}>
                  {tagInfo.lotteryEnrolled ? '🎰' : '🎫'}
                </div>
                <div className="flex-1">
                  <h3 className={`font-bold text-lg ${
                    tagInfo.lotteryEnrolled ? 'text-purple-800' : 'text-gray-900'
                  }`}>
                    {tagInfo.lotteryEnrolled ? 'In Lottery' : 'Enter Lottery'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {tagInfo.lotteryEnrolled
                      ? 'Tag is already enrolled in active lottery'
                      : 'Enter tag into active lottery campaign. Generates lottery number for customer.'}
                  </p>
                </div>
              </div>
              {lotteryLoading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-3">
            {results.map((result, i) => (
              <div
                key={i}
                className={`p-4 rounded-xl border ${
                  result.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg">
                    {result.success
                      ? result.type === 'warranty' ? '🛡️' : '🎫'
                      : '❌'}
                  </span>
                  <div>
                    <p className={`font-semibold text-sm ${
                      result.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {result.type === 'warranty' ? 'WARRANTY' : 'LOTTERY'}: {result.message}
                    </p>
                    {result.lotteryNumber && (
                      <p className="text-sm text-gray-600 mt-1">
                        🎟️ Lottery: {result.lotteryName} — Number: <strong>{result.lotteryNumber}</strong>
                      </p>
                    )}
                    {result.warrantyExpiry && (
                      <p className="text-sm text-gray-600 mt-1">
                        📅 Warranty expires: <strong>{result.warrantyExpiry}</strong>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={handleReset}
              className="w-full py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-900 transition-colors"
            >
              🔄 Scan Next Tag
            </button>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
          <p className="text-sm text-blue-800 font-semibold mb-2">ℹ️ How it works:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-blue-700">
            <div>
              <p className="font-semibold mb-1">🛡️ Warranty Activation</p>
              <ul className="space-y-0.5 ml-3">
                <li>• Links tag to this store + date</li>
                <li>• Records customer info (optional)</li>
                <li>• Activates product warranty</li>
                <li>• Customer can verify via scan</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-1">🎫 Lottery Entry</p>
              <ul className="space-y-0.5 ml-3">
                <li>• GPS validates store location</li>
                <li>• Tag enters active campaign</li>
                <li>• Lottery number generated</li>
                <li>• Winner drawn on campaign end date</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreActivateTag;